/* eslint-disable no-underscore-dangle */
/**
 * Role-based permission system for event staff.
 *
 * Each assignment has:
 *   - role:        "manager" | "scanner" | "runner" | "usher"
 *   - permissions: string[] — granular permission keys granted to this user
 *   - status:      "accepted" | "pending" | ...
 *
 * Granular permission keys:
 *   bookingOnBehalf   — book tickets on behalf of attendees
 *   qrScanning        — scan QR codes at entry
 *   cancellationRights — cancel/modify tickets
 *   reportsAccess     — view analytics and reports
 *   viewOnly          — read-only access (overrides other actions)
 *
 * Access matrix (default role defaults, overridden by permissions array):
 * | Permission         | Manager | Scanner | Runner | Usher |
 * | Edit event         | ✓       | ✗       | ✗      | ✗     |
 * | Delete event       | ✓       | ✗       | ✗      | ✗     |
 * | Manage roles       | ✓       | ✗       | ✗      | ✗     |
 * | qrScanning         | per permissions array                |
 * | cancellationRights | per permissions array                |
 * | reportsAccess      | per permissions array                |
 *
 * Users with no accepted assignment who are the event owner/creator receive
 * full access to everything.
 */

const ROLE = {
  MANAGER: 'manager',
  SCANNER: 'scanner',
  RUNNER: 'runner',
  USHER: 'usher',
};

const normalize = (role) => (role || '').toString().trim().toLowerCase();

/**
 * Get the current user's accepted role assignment for an event.
 * Returns null if the user has no accepted assignment.
 */
export const getEventRoleForUser = (event, currentUserId) => {
  if (!event || !currentUserId) return null;
  const assignments = event.managerAssignments;
  if (!Array.isArray(assignments)) return null;
  const uid = String(currentUserId).trim();
  const assignment = assignments.find((a) => {
    const mid =
      a.managerId != null ? (a.managerId._id || a.managerId.id || a.managerId).toString() : '';
    return mid === uid && a.status === 'accepted';
  });
  return assignment || null;
};

/** True when the user is the event owner or creator (no role assignment required). */
const isOwnerOrCreator = (event, uid) => {
  if (!event || !uid) return false;
  const uidStr = String(uid).trim();
  const inCreatedBy =
    Array.isArray(event.createdBy) &&
    event.createdBy.some((id) => id != null && (id.toString?.() || String(id)) === uidStr);
  const eventOwnerId =
    event.eventOwner != null
      ? (event.eventOwner._id || event.eventOwner.id || event.eventOwner).toString()
      : '';
  return inCreatedBy || eventOwnerId === uidStr;
};

const hasPermissionKey = (assignment, key) =>
  Array.isArray(assignment?.permissions) && assignment.permissions.includes(key);

const isViewOnly = (assignment) => hasPermissionKey(assignment, 'viewOnly');

/**
 * EDIT EVENT
 * Allowed: Manager role without viewOnly (or original owner/creator with no assignment).
 * Denied:  Scanner, Runner, Usher; Manager with viewOnly.
 */
export const canEditEvent = (event, currentUserId) => {
  if (!event || !currentUserId) return false;
  const assignment = getEventRoleForUser(event, currentUserId);
  if (assignment) return normalize(assignment.role) === ROLE.MANAGER && !isViewOnly(assignment);
  return isOwnerOrCreator(event, currentUserId);
};

/**
 * DELETE EVENT
 * Allowed: Manager role without viewOnly (or original owner/creator with no assignment).
 * Denied:  Scanner, Runner, Usher; Manager with viewOnly.
 */
export const canDeleteEvent = (event, currentUserId) => {
  if (!event || !currentUserId) return false;
  const assignment = getEventRoleForUser(event, currentUserId);
  if (assignment) return normalize(assignment.role) === ROLE.MANAGER && !isViewOnly(assignment);
  return isOwnerOrCreator(event, currentUserId);
};

/**
 * MANAGE ROLES (roles-and-permission page)
 * Allowed: Manager role without viewOnly (or original owner/creator with no assignment).
 * Denied:  Scanner, Runner, Usher; Manager with viewOnly.
 */
export const canManageRoles = (event, currentUserId) => {
  if (!event || !currentUserId) return false;
  const assignment = getEventRoleForUser(event, currentUserId);
  if (assignment) return normalize(assignment.role) === ROLE.MANAGER && !isViewOnly(assignment);
  return isOwnerOrCreator(event, currentUserId);
};

/**
 * CANCEL EVENT / REQUEST EVENT CANCELLATION
 * Driven by the cancellationRights permission key.
 * Original owner/creator (with no assignment) always allowed.
 */
export const canCancelEvent = (event, currentUserId) => {
  if (!event || !currentUserId) return false;
  const assignment = getEventRoleForUser(event, currentUserId);
  if (assignment) return hasPermissionKey(assignment, 'cancellationRights');
  return isOwnerOrCreator(event, currentUserId);
};

/**
 * SCAN TICKETS
 * Driven by the qrScanning permission key.
 * Original owner/creator (with no assignment) always allowed.
 */
export const canScanTickets = (event, currentUserId) => {
  if (!event || !currentUserId) return false;
  const assignment = getEventRoleForUser(event, currentUserId);
  if (assignment) return hasPermissionKey(assignment, 'qrScanning');
  return isOwnerOrCreator(event, currentUserId);
};

/**
 * VIEW ANALYTICS
 * Driven by the reportsAccess permission key.
 * Original owner/creator (with no assignment) always allowed.
 */
export const canViewAnalytics = (event, currentUserId) => {
  if (!event || !currentUserId) return false;
  const assignment = getEventRoleForUser(event, currentUserId);
  if (assignment) return hasPermissionKey(assignment, 'reportsAccess');
  return isOwnerOrCreator(event, currentUserId);
};
